/**
 * Payment-Link Link-Tester — Service-Datenpfad (Spec 501, Phase 5).
 *
 * Joint die payment-link-freigeschalteten Services (`usePaymentLinkServices`) mit
 * den Service-Metadaten (`listServices`) fuer den Service-Picker + Prefill. Die
 * PL-Liste ist autoritativ (welche Services); die Metadaten (DisplayName, Betrag,
 * Intervall) sind best-effort — fehlt eine Metadatenzeile, bleibt der Prefill
 * einfach leer, das Werkzeug funktioniert trotzdem.
 */
import { useQuery } from '@tanstack/react-query';
import { usePaymentLinkServices } from '@/hooks/use-payment-link';
import { listServices } from '@/lib/api/services';
import type { PaymentLinkIdempotencyMode } from '@/types/payment-link';
import type { SubscriptionInterval } from '@/types/service';

export interface TesterServiceOption {
  serviceId: string;
  merchantId: string;
  displayName?: string;
  /** Bruttobetrag in Cent (Prefill). */
  grossAmountCents?: number;
  interval?: SubscriptionInterval;
  idempotencyMode: PaymentLinkIdempotencyMode;
  hasClickWebhook: boolean;
}

export interface TesterServicesResult {
  options: TesterServiceOption[];
  isLoading: boolean;
  /** Nur die autoritative PL-Liste zaehlt als Fehler; Metadaten sind best-effort. */
  isError: boolean;
}

export function useTesterServices(): TesterServicesResult {
  const pl = usePaymentLinkServices();
  const services = useQuery({
    queryKey: ['services', 'link-tester-metadata'],
    queryFn: () => listServices({ limit: 100 }),
    staleTime: 30_000,
  });

  const metaById = new Map(
    (services.data?.data ?? []).map((s) => [s.serviceId, s]),
  );

  const options: TesterServiceOption[] = (pl.data?.Services ?? []).map((c) => {
    const meta = metaById.get(c.ServiceId);
    return {
      serviceId: c.ServiceId,
      merchantId: c.MerchantId,
      displayName: meta?.displayName,
      grossAmountCents: meta?.grossAmount,
      interval: meta?.subscriptionInterval,
      idempotencyMode: c.IdempotencyMode,
      hasClickWebhook: c.ClickNotification?.Enabled ?? false,
    };
  });

  return {
    options,
    isLoading: pl.isLoading || services.isLoading,
    isError: pl.isError,
  };
}
